import { Braces } from "lucide-react";
import { CitationList, EmptyState, FieldRow, SectionCard, SourceChip } from "@/components/coverage/coverageUi";

function SubBlock({ title, section }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">{title}</p>
      {!section?.present ? (
        <p className="text-xs text-slate-500">Not provided</p>
      ) : (
        <div className="space-y-1.5">
          {(section.fields || []).map((field) => (
            <FieldRow key={field.label} label={field.label} value={field.value} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrthoPolicyPanel({ ortho }) {
  const sourceLabel =
    ortho?.source === "lakehouse"
      ? "Lakehouse ortho policy"
      : ortho?.source === "stedi"
        ? "Stedi 271 category"
        : "Not on payload";

  return (
    <SectionCard
      testId="coverage-ortho-panel"
      className="border-emerald-500/30 bg-emerald-500/5"
      title="Orthodontics / ortho policy"
      icon={<Braces className="w-4 h-4 text-emerald-400" />}
      badge={<SourceChip>{sourceLabel}</SourceChip>}
    >
      {!ortho?.present ? (
        <EmptyState
          title="No orthodontist policy on this coverage record"
          detail="When Stedi category 38 or Databricks lakehouse ortho (lifetime max, age limits, phase I/II, banding, retention) is present, it appears here. Values are not invented."
        />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-bold">Plan coverage IN / OON</p>
              <p className="text-lg font-semibold text-emerald-400 mt-1">
                {ortho.plan_in} <span className="text-slate-500 font-normal">/</span> {ortho.plan_out}
              </p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-bold">Member coinsurance IN / OON</p>
              <p className="text-lg font-semibold text-slate-100 mt-1">
                {ortho.member_in} <span className="text-slate-500 font-normal">/</span> {ortho.member_out}
              </p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-bold">Lifetime max</p>
              <p className="text-lg font-semibold text-slate-100 mt-1">{ortho.lifetime_max}</p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-bold">Lifetime remaining</p>
              <p className="text-lg font-semibold text-slate-100 mt-1">{ortho.remaining_lifetime_max}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <FieldRow label="Covered" value={ortho.covered} />
            <FieldRow label="Deductible" value={ortho.deductible} />
            <FieldRow
              label="Age limits"
              value={ortho.age_limits?.length ? ortho.age_limits.join("; ") : "Not provided"}
            />
            {ortho.notes ? <FieldRow label="Notes" value={ortho.notes} /> : null}
            {(ortho.extra_fields || []).map((field) => (
              <FieldRow key={field.label} label={field.label} value={field.value} />
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <SubBlock title="Phase I" section={ortho.phase_i} />
            <SubBlock title="Phase II" section={ortho.phase_ii} />
            <SubBlock title="Banding" section={ortho.banding} />
            <SubBlock title="Appliance" section={ortho.appliance} />
            <SubBlock title="Retention" section={ortho.retention} />
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
              Documentation
            </p>
            {!ortho.documentation?.length ? (
              <EmptyState title="No ortho documentation requirements on this payload" />
            ) : (
              <div className="space-y-2">
                {ortho.documentation.map((doc, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-800 p-3 text-sm text-slate-300">
                    <p>{doc.text}</p>
                    {doc.notes ? <p className="text-xs text-slate-500 mt-1">{doc.notes}</p> : null}
                    <CitationList citations={doc.citations} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {(ortho.limitations || []).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Stedi ortho limitations
              </p>
              <div className="space-y-2">
                {ortho.limitations.map((lim, idx) => (
                  <div key={idx} className="text-sm text-slate-300 border-b border-slate-800/80 pb-2">
                    <p>{lim.text || "Limitation"}</p>
                    {lim.quantity ? <p className="text-xs text-slate-500">{lim.quantity}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {ortho.citations?.length ? (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Ortho citations
              </p>
              <CitationList citations={ortho.citations} />
            </div>
          ) : ortho.source === "lakehouse" ? (
            <EmptyState title="Lakehouse ortho block has no citations yet" />
          ) : null}
        </>
      )}
    </SectionCard>
  );
}
